<template>
  <BaseModal
    :visible="visible"
    :title="titulo"
    max-width="max-w-[960px]"
    persistent
    @close="$emit('close')"
  >
    <form novalidate class="cf" data-testid="cliente-form" @submit.prevent="alGuardar">
      <section class="cf-seccion" aria-label="Datos del cliente">
        <div class="cf-grid">
          <AppField
            v-model="form.name"
            class="c2"
            label="Nombre"
            required
            placeholder="Juan"
            :error="mostrarError('name')"
            testid="form-nombre"
            @blur="alSalirDeNombre('name')"
          />
          <AppField
            v-model="form.first_lastname"
            class="c2"
            label="Apellido paterno"
            required
            placeholder="López"
            :error="mostrarError('first_lastname')"
            testid="form-apellido-paterno"
            @blur="alSalirDeNombre('first_lastname')"
          />
          <AppField
            v-model="form.second_lastname"
            class="c2"
            label="Apellido materno"
            placeholder="Pérez"
            testid="form-apellido-materno"
            @blur="alSalirDeNombre('second_lastname')"
          />
          <AppField
            v-model="telefonoVisible"
            class="c1"
            label="Celular"
            required
            type="tel"
            inputmode="numeric"
            maxlength="14"
            placeholder="(667) 319 0560"
            :format="mascaraTelefono"
            :error="mostrarError('telephones')"
            testid="form-celular"
            @blur="tocar('telephones')"
          />
          <AppField
            v-model="nacimiento"
            class="c1"
            label="Nacimiento"
            inputmode="numeric"
            maxlength="10"
            placeholder="DD/MM/AAAA"
            :format="mascaraFecha"
            :error="mostrarError('birthdate')"
            testid="form-nacimiento"
            @blur="tocar('birthdate')"
          />
          <AppField
            v-slot="{ controlId }"
            :class="mostrarDireccionLibre ? 'c2' : 'c4 ancho-medio'"
            label="Correo"
            :required="requiereFactura"
            :error="mostrarError('email')"
            testid="form-correo"
          >
            <!-- Sugiere el dominio al escribir "@" y ofrece corregir errores comunes -->
            <AppCorreo
              :id="controlId"
              v-model="form.email"
              :invalid="Boolean(mostrarError('email'))"
              testid="form-correo"
              @blur="tocar('email')"
            />
          </AppField>
          <!-- Con factura, la dirección se captura una sola vez en el domicilio fiscal -->
          <AppField
            v-if="mostrarDireccionLibre"
            v-model="form.address"
            class="c2 ancho-medio"
            label="Dirección"
            placeholder="Calle, número y colonia"
            testid="form-direccion"
            @blur="form.address = capitalizarDireccion(form.address)"
          />
        </div>
      </section>

      <section class="cf-seccion" aria-labelledby="cf-titulo-factura">
        <label class="cf-switch">
          <input
            type="checkbox"
            role="switch"
            :checked="requiereFactura"
            data-testid="form-requiere-factura"
            @change="alternarFactura($event.target.checked)"
          />
          <span class="cf-switch-pista" aria-hidden="true"></span>
          <span class="cf-switch-texto">
            <span id="cf-titulo-factura" class="cf-titulo">Requiere factura</span>
            <span class="cf-ayuda">
              {{ textoAyudaFactura }}
            </span>
          </span>
        </label>

        <div v-if="requiereFactura" class="cf-grid cf-grid-fiscal" data-testid="form-bloque-fiscal">
          <AppField
            v-model="form.razon_social"
            class="c2 ancho-medio"
            label="Razón social"
            required
            uppercase
            :error="mostrarError('razon_social')"
            testid="form-razon-social"
            @blur="tocar('razon_social')"
          />
          <AppField
            v-model="form.rfc"
            class="c2"
            label="RFC"
            required
            uppercase
            maxlength="13"
            :error="mostrarError('rfc')"
            testid="form-rfc"
            @blur="tocar('rfc')"
          />
          <AppField
            v-slot="{ controlId }"
            class="c2"
            label="Régimen fiscal"
            required
            :error="mostrarError('regimen_fiscal')"
            testid="form-regimen"
          >
            <!-- 23 regímenes: select con buscador por clave o nombre -->
            <AppCombobox
              :id="controlId"
              v-model="form.regimen_fiscal"
              :options="opcionesRegimen"
              placeholder="Busca por clave o nombre"
              :invalid="Boolean(mostrarError('regimen_fiscal'))"
              testid="form-regimen"
              @blur="tocar('regimen_fiscal')"
            />
          </AppField>
          <template v-if="!esPublicoGeneral">
            <AppField
              v-model="form.calle"
              class="c3"
              label="Calle"
              required
              uppercase
              :error="mostrarError('calle')"
              testid="form-calle"
              @blur="tocar('calle')"
            />
            <AppField
              v-model="form.numero_exterior"
              class="c1"
              label="Núm. exterior"
              required
              uppercase
              placeholder="#"
              :error="mostrarError('numero_exterior')"
              testid="form-num-ext"
              @blur="tocar('numero_exterior')"
            />
            <AppField
              v-model="form.numero_interior"
              class="c1"
              label="Núm. interior"
              placeholder="#"
              testid="form-num-int"
            />
            <AppField
              v-model="form.codigo_postal"
              class="c1"
              label="Código postal"
              required
              inputmode="numeric"
              maxlength="5"
              placeholder="65000"
              :error="mostrarError('codigo_postal')"
              testid="form-cp"
              @blur="tocar('codigo_postal')"
            />
            <AppField
              v-model="form.colonia"
              class="c2"
              label="Colonia"
              required
              uppercase
              :error="mostrarError('colonia')"
              testid="form-colonia"
              @blur="tocar('colonia')"
            />
            <AppField
              v-model="form.municipio"
              class="c2"
              label="Municipio"
              required
              uppercase
              :error="mostrarError('municipio')"
              testid="form-municipio"
              @blur="tocar('municipio')"
            />
            <AppField
              v-slot="{ controlId }"
              class="c2"
              label="Estado"
              required
              :error="mostrarError('estado')"
              testid="form-estado"
            >
              <AppCombobox
                :id="controlId"
                v-model="form.estado"
                :options="opcionesEstado"
                placeholder="Busca un estado"
                :invalid="Boolean(mostrarError('estado'))"
                testid="form-estado"
                @blur="tocar('estado')"
              />
            </AppField>
          </template>
          <p v-else class="cf-nota c6" data-testid="form-nota-publico-general">
            RFC genérico de público en general: no se pide domicilio fiscal.
          </p>
        </div>
      </section>
    </form>
    <template #footer>
      <button type="button" class="rm-btn rm-btn-secundario" data-testid="form-cancelar" @click="$emit('close')">
        Cancelar
      </button>
      <button
        type="button"
        class="rm-btn rm-btn-primario"
        :disabled="guardarDeshabilitado || saving"
        data-testid="form-guardar"
        @click="alGuardar"
      >
        {{ saving ? 'Guardando…' : 'Guardar' }}
      </button>
    </template>
  </BaseModal>
</template>

<script>
/*
 * ClienteFormModal — alta/edición de cliente (reemplaza el v-dialog de Vuetify).
 *
 * - Misma regla de habilitación del Guardar que la pantalla original
 *   (isDisabled): mínimo nombre + apellido + celular de 10 dígitos; el bloque
 *   fiscal se vuelve obligatorio cuando hay razón social; el RFC de público
 *   general (XAXX010101000) oculta el domicilio fiscal.
 * - Los datos fiscales viven detrás del interruptor "Requiere factura"
 *   (la mayoría de los clientes del taller no factura). Encendido exige
 *   razón social; apagado limpia el bloque y lo respalda por si se vuelve a
 *   encender antes de guardar. Así el formulario cabe sin scroll.
 * - `uppercase` replica la directiva v-uppercase de los campos fiscales.
 * - El teléfono conserva la forma del registro original: si el cliente venía
 *   con arreglo y no se toca el campo, sale arreglo; al teclear, sale texto
 *   (mismo comportamiento que el v-model del v-text-field viejo).
 * - Celular con máscara (667) 319 0560 y nacimiento en DD/MM/AAAA, sin
 *   depender del idioma del navegador; se guardan como 10 dígitos e ISO.
 * - Nombre y apellidos se capitalizan al salir del campo ("ana lópez" →
 *   "Ana López").
 * - La dirección se pide una sola vez. Sin factura, en el campo libre
 *   (`address`, el que imprime el contrato de la orden). Con factura, en el
 *   domicilio fiscal estructurado (lo exige el timbrado) y al guardar se
 *   copia armado a `address`. Apagar la factura pasa el domicilio al campo
 *   libre para no perderlo.
 * - Estado: solo los de México, con buscador y Sinaloa por defecto (por eso
 *   el país ya no se pide: siempre se guarda "México"). Se
 *   guarda como los registros existentes ("NUEVO LEON"); al editar se
 *   reconocen variantes ("Nuevo León", "CDMX"). Lo que no es estado se
 *   muestra marcado y pide elegir uno de la lista antes de guardar.
 * - Correo con autocompletado de dominio y "¿Quisiste decir…?" para los
 *   errores frecuentes de la base (hotmial.com, hotmail sin .com).
 * - Emite `save` con el payload completo; el padre decide crear o editar.
 */
import AppCombobox from '@/components/ui/AppCombobox.vue'
import AppCorreo from '@/components/ui/AppCorreo.vue'
import AppField from '@/components/ui/AppField.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import { validateNoErrors } from '@/helpers/helpers'
import { ESTADO_PREDETERMINADO, ESTADOS_MEXICO, esEstadoValido, normalizarEstado } from '@/utils/estados'
import {
  capitalizarDireccion,
  capitalizarNombre,
  componerDireccion,
  fechaTextoAISO,
  isoAFechaTexto,
  mascaraFecha,
  mascaraTelefono,
} from '@/utils/formato'

const RFC_PUBLICO_GENERAL = 'XAXX010101000'

const CAMPOS_FISCALES = [
  'razon_social',
  'rfc',
  'regimen_fiscal',
  'codigo_postal',
  'calle',
  'numero_exterior',
  'numero_interior',
  'colonia',
  'municipio',
  'estado',
]

const REGIMENES_FISCALES = [
  { value: 601, text: 'General de Ley Personas Morales' },
  { value: 603, text: 'Personas Morales con Fines no Lucrativos' },
  { value: 605, text: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { value: 606, text: 'Arrendamiento' },
  { value: 607, text: 'Régimen de Enajenación o Adquisición de Bienes' },
  { value: 608, text: 'Demás ingresos' },
  { value: 609, text: 'Consolidación' },
  { value: 610, text: 'Residentes en el Extranjero sin Establecimiento Permanente en México' },
  { value: 611, text: 'Ingresos por Dividendos (socios y accionistas)' },
  { value: 612, text: 'Personas Físicas con Actividades Empresariales y Profesionales' },
  { value: 614, text: 'Ingresos por intereses' },
  { value: 615, text: 'Régimen de los ingresos por obtención de premios' },
  { value: 616, text: 'Sin obligaciones fiscales' },
  { value: 620, text: 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos' },
  { value: 621, text: 'Incorporación Fiscal' },
  { value: 622, text: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { value: 623, text: 'Opcional para Grupos de Sociedades' },
  { value: 624, text: 'Coordinados' },
  { value: 625, text: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { value: 626, text: 'Régimen Simplificado de Confianza' },
  { value: 628, text: 'Hidrocarburos' },
  { value: 629, text: 'De los Regímenes Fiscales Preferentes y de las Empresas Multinacionales' },
  { value: 630, text: 'Enajenación de acciones en bolsa de valores' },
]

function formularioVacio() {
  return {
    name: '',
    first_lastname: '',
    second_lastname: '',
    birthdate: '',
    address: '',
    telephones: '',
    email: '',
    razon_social: '',
    rfc: '',
    regimen_fiscal: '',
    codigo_postal: '',
    calle: '',
    numero_exterior: '',
    numero_interior: '',
    colonia: '',
    municipio: '',
    estado: '',
    pais: 'México',
  }
}

/* Equivalente a moment(fecha).utc().format('YYYY-MM-DD') de la pantalla vieja */
function fechaISO(fecha) {
  if (!fecha) return ''
  const d = new Date(fecha)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

export default {
  name: 'ClienteFormModal',
  components: { AppCombobox, AppCorreo, AppField, BaseModal },
  props: {
    visible: { type: Boolean, default: false },
    cliente: { type: Object, default: null }, // null = alta nueva
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      form: formularioVacio(),
      nacimientoTexto: '',
      requiereFactura: false,
      respaldoFiscal: null,
      tocados: {},
      intentoGuardar: false,
    }
  },
  computed: {
    opcionesEstado() {
      const actual = this.form.estado
      // Un valor histórico que no es estado se muestra para no ocultar el dato
      if (actual && !esEstadoValido(actual)) {
        return [{ value: actual, label: `${actual} (no es un estado)` }, ...ESTADOS_MEXICO]
      }
      return ESTADOS_MEXICO
    },
    opcionesRegimen() {
      return REGIMENES_FISCALES.map((r) => ({ value: r.value, codigo: String(r.value), label: r.text }))
    },
    titulo() {
      return this.cliente ? 'Editar cliente' : 'Nuevo cliente'
    },
    // Fecha local (no UTC): de noche en México el día UTC ya es mañana
    hoy() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    esPublicoGeneral() {
      return this.form.rfc === RFC_PUBLICO_GENERAL
    },
    bloqueFiscal() {
      return this.requiereFactura
    },
    // Público general no lleva domicilio fiscal: ahí sigue el campo libre
    mostrarDireccionLibre() {
      return !this.requiereFactura || this.esPublicoGeneral
    },
    textoAyudaFactura() {
      if (!this.requiereFactura) return 'Actívalo solo si el cliente pide factura.'
      if (this.esPublicoGeneral) return 'Público en general: solo razón social y régimen.'
      return 'El domicilio fiscal también se usa en el contrato de la orden.'
    },
    telefono: {
      get() {
        const t = this.form.telephones
        return Array.isArray(t) ? t[0] || '' : t || ''
      },
      set(valor) {
        this.form.telephones = valor
      },
    },
    // Lo que se ve en el input: con máscara; al modelo solo llegan los dígitos
    telefonoVisible: {
      get() {
        return mascaraTelefono(this.telefono)
      },
      set(valor) {
        this.telefono = String(valor).replace(/\D/g, '')
      },
    },
    nacimiento: {
      get() {
        return this.nacimientoTexto
      },
      set(valor) {
        this.nacimientoTexto = valor
        this.form.birthdate = fechaTextoAISO(valor) || ''
      },
    },
    errores() {
      const f = this.form
      const e = {}
      if (this.nacimientoTexto) {
        const iso = fechaTextoAISO(this.nacimientoTexto)
        if (!iso) e.birthdate = 'Usa el formato DD/MM/AAAA.'
        else if (iso < '1930-01-01' || iso > this.hoy) e.birthdate = 'Fecha fuera de rango.'
      }
      if (!f.name) e.name = 'Requerido.'
      if (!f.first_lastname) e.first_lastname = 'Requerido.'
      if (!this.telefono) e.telephones = 'Requerido.'
      else if (!validateNoErrors('telephones', this.telefono)) e.telephones = 'Deben ser 10 digitos'
      if (f.email && !validateNoErrors('email', f.email)) e.email = 'Formato no válido'
      if (this.bloqueFiscal && !f.email) e.email = 'Requerido.'
      if (this.bloqueFiscal && !f.razon_social) e.razon_social = 'Requerido.'
      else if (f.razon_social && !validateNoErrors('razonSocial', f.razon_social)) e.razon_social = 'Formato no válido'
      if (!f.rfc && this.bloqueFiscal) e.rfc = 'Requerido.'
      else if (f.rfc && !validateNoErrors('rfc', f.rfc)) e.rfc = 'Formato no válido'
      if (this.bloqueFiscal && !f.regimen_fiscal) e.regimen_fiscal = 'Requerido.'
      if (this.bloqueFiscal && !this.esPublicoGeneral) {
        for (const campo of ['codigo_postal', 'calle', 'numero_exterior', 'colonia', 'municipio', 'estado']) {
          if (!f[campo]) e[campo] = 'Requerido.'
        }
        if (f.estado && !esEstadoValido(f.estado)) e.estado = 'Elige un estado de la lista.'
      }
      return e
    },
    // Misma lógica que el isDisabled() de la pantalla original
    guardarDeshabilitado() {
      const f = this.form
      // Con el interruptor encendido la razón social es la llave del bloque fiscal
      if (this.requiereFactura && !f.razon_social) return true
      if (this.errores.birthdate) return true
      if (this.requiereFactura && !this.esPublicoGeneral && f.estado && !esEstadoValido(f.estado)) return true
      const telefonoValido = validateNoErrors('telephones', f.telephones)

      if (f.razon_social) {
        const validRfc = validateNoErrors('rfc', f.rfc)
        const validNombre = validateNoErrors('razonSocial', f.razon_social)
        const correoValido = validateNoErrors('email', f.email)

        if (
          this.esPublicoGeneral &&
          f.name &&
          f.first_lastname &&
          f.telephones &&
          telefonoValido &&
          f.regimen_fiscal &&
          correoValido &&
          validNombre
        )
          return false

        if (
          f.name &&
          f.first_lastname &&
          f.telephones &&
          telefonoValido &&
          validRfc &&
          validNombre &&
          f.codigo_postal &&
          f.regimen_fiscal &&
          f.calle &&
          f.numero_exterior &&
          f.colonia &&
          f.municipio &&
          correoValido &&
          f.estado
        )
          return false
      }

      if (f.email) {
        const correoValido = validateNoErrors('email', f.email)
        if (
          f.name &&
          f.first_lastname &&
          f.telephones &&
          telefonoValido &&
          correoValido &&
          !f.rfc &&
          !f.razon_social &&
          !f.regimen_fiscal
        )
          return false
      }

      if (
        f.name &&
        f.first_lastname &&
        f.telephones &&
        telefonoValido &&
        !f.email &&
        !f.rfc &&
        !f.razon_social &&
        !f.regimen_fiscal
      )
        return false

      return true
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(abierto) {
        if (!abierto) return
        // Se parte del formulario vacío: alternarFactura asigna los campos
        // fiscales directo y deben existir aunque el registro histórico no los traiga
        this.form = this.cliente
          ? { ...formularioVacio(), ...this.cliente, birthdate: fechaISO(this.cliente.birthdate), pais: 'México' }
          : formularioVacio()
        this.nacimientoTexto = isoAFechaTexto(this.form.birthdate)
        // Variantes viejas ("Nuevo León", "CIUDAD DE  MEXICO") al valor del catálogo
        this.form.estado = normalizarEstado(this.form.estado) || this.form.estado || ''
        this.requiereFactura = Boolean(
          this.cliente && (this.cliente.razon_social || this.cliente.rfc || this.cliente.regimen_fiscal)
        )
        this.respaldoFiscal = null
        if (this.requiereFactura && !this.form.estado) this.form.estado = ESTADO_PREDETERMINADO
        this.tocados = {}
        this.intentoGuardar = false
      },
    },
  },
  methods: {
    alternarFactura(activar) {
      if (activar) {
        if (this.respaldoFiscal) Object.assign(this.form, this.respaldoFiscal)
        this.respaldoFiscal = null
        if (!this.form.estado) this.form.estado = ESTADO_PREDETERMINADO
      } else {
        const respaldo = {}
        for (const campo of CAMPOS_FISCALES) respaldo[campo] = this.form[campo]
        // El domicilio capturado no se pierde: pasa al campo de dirección libre
        // (solo si hay domicilio real: el estado por defecto no cuenta)
        const hayDomicilio = respaldo.calle || respaldo.colonia || respaldo.municipio
        if (!this.form.address && hayDomicilio) this.form.address = componerDireccion(respaldo).texto
        for (const campo of CAMPOS_FISCALES) this.form[campo] = ''
        this.respaldoFiscal = Object.values(respaldo).some(Boolean) ? respaldo : null
      }
      this.requiereFactura = activar
    },
    capitalizarDireccion,
    mascaraFecha,
    mascaraTelefono,
    alSalirDeNombre(campo) {
      this.form[campo] = capitalizarNombre(this.form[campo])
      this.tocar(campo)
    },
    tocar(campo) {
      this.tocados = { ...this.tocados, [campo]: true }
    },
    mostrarError(campo) {
      return this.tocados[campo] || this.intentoGuardar ? this.errores[campo] || '' : ''
    },
    alGuardar() {
      this.intentoGuardar = true
      if (this.guardarDeshabilitado) return
      const payload = { ...this.form }
      if (!this.mostrarDireccionLibre) payload.address = componerDireccion(this.form).texto
      this.$emit('save', payload)
    },
  },
}
</script>

<style scoped>
.cf {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.cf-seccion + .cf-seccion {
  padding-top: 16px;
  border-top: 1px solid #eef2f6;
}
.cf-titulo {
  display: block;
  margin: 0 0 10px;
  color: #0b1b2e;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}
.cf-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px 14px;
}
.cf-grid-fiscal {
  margin-top: 14px;
}
.c1 { grid-column: span 1; }
.c2 { grid-column: span 2; }
.c3 { grid-column: span 3; }
.c4 { grid-column: span 4; }
.c6 { grid-column: 1 / -1; }
.cf-nota {
  align-self: end;
  margin: 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f3f5f8;
  color: #3d4b5f;
  font-size: 13px;
}
.cf-switch {
  position: relative;
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}
.cf-switch input {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  opacity: 0;
}
.cf-switch-pista {
  position: relative;
  width: 40px;
  height: 24px;
  flex: none;
  border-radius: 999px;
  background: #d8e0ea;
  transition: background 0.16s ease;
}
.cf-switch-pista::after {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 42, 67, 0.25);
  content: '';
  transition: transform 0.16s ease;
}
.cf-switch input:checked + .cf-switch-pista {
  background: #0a4f9c;
}
.cf-switch input:checked + .cf-switch-pista::after {
  transform: translateX(16px);
}
.cf-switch input:focus-visible + .cf-switch-pista {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
.cf-switch-texto {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.cf-switch-texto .cf-titulo {
  margin: 0;
}
.cf-ayuda {
  color: #607187;
  font-size: 12px;
}
@media (prefers-reduced-motion: reduce) {
  .cf-switch-pista,
  .cf-switch-pista::after {
    transition: none;
  }
}
/* Modal más angosto que la rejilla de 6: pasa a 2 columnas */
@media (max-width: 859px) {
  .cf-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .c1,
  .c2,
  .c3,
  .c4 {
    grid-column: span 1;
  }
  .ancho-medio,
  .c6 {
    grid-column: 1 / -1;
  }
}
@media (max-width: 559px) {
  .cf-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .c1,
  .c2,
  .c3,
  .c4,
  .ancho-medio {
    grid-column: 1 / -1;
  }
}
</style>
